// Geometry is expressed in export pixels; preview and PNG use the same bounds.
export const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const FORMATS = [
  { id: "auto", label: "Original · Auto" },
  { id: "square", label: "Square · 1:1", width: 1600, height: 1600 },
  { id: "portrait", label: "Portrait · 4:5", width: 1280, height: 1600 },
  { id: "story", label: "Story · 9:16", width: 900, height: 1600 },
  { id: "landscape", label: "Landscape · 16:9", width: 1600, height: 900 },
  { id: "photo", label: "Photo · 3:2", width: 1500, height: 1000 },
  { id: "custom", label: "Custom dimensions" },
];

export const LAYOUTS = [
  { id: "classic", name: "Classic", description: "The familiar playful arrangement." },
  { id: "gallery", name: "Gallery", description: "Even rows, with every card readable." },
  { id: "fan", name: "Fan", description: "A gentle arc with room for card text." },
  { id: "spotlight", name: "Spotlight", description: "A bigger main card, companions alongside." },
  { id: "orbit", name: "Orbit", description: "Main card in the middle, companions dancing around it." },
  { id: "cascade", name: "Cascade", description: "Staggered heights and alternating tilts." },
];

export const RARITIES = [
  { id: "free", label: "Free", color: "#edf4ff" },
  { id: "common", label: "Common", color: "#cbd3df" },
  { id: "rare", label: "Rare", color: "#409cff" },
  { id: "epic", label: "Epic", color: "#bf65ff" },
  { id: "legendary", label: "Legendary", color: "#ffac38" },
];

export const getRarity = (data) => {
  const names = [data.cardRarity, data.rarity, data.Rarity, data.cardrarity, data.gem, data.Gem]
    .map((value) => String(value ?? "").trim().toLowerCase());
  return names.find((name) => RARITIES.some((rarity) => rarity.id === name)) || "";
};

export const MIN_EXPORT_CARD_WIDTH = 350;
const MAX_EXPORT_SIDE = 8192;
const MAX_EXPORT_PIXELS = 40000000;

export const getExportResolution = (elements, scene, images, trim, minimumWidth = MIN_EXPORT_CARD_WIDTH) => {
  if (minimumWidth <= 0) return { width: scene.width, height: scene.height, scale: 1, meetsMinimum: true };
  const widths = elements.filter((element) => element.kind === "card").map((element) => {
    const metrics = images[element.id];
    const crop = metrics?.exportCrop || getImageCrop(metrics, trim);
    // The minimum refers to the artwork, even when original padding is shown.
    const visibleFraction = metrics ? metrics.bounds.width / crop.width : 1;
    return scene.baseCardWidth * element.scale * visibleFraction;
  }).filter((width) => Number.isFinite(width) && width > 0);
  const smallestWidth = widths.length ? Math.min(...widths) : minimumWidth;
  const requiredScale = Math.max(1, minimumWidth / smallestWidth);
  const maximumScale = Math.min(
    MAX_EXPORT_SIDE / scene.width,
    MAX_EXPORT_SIDE / scene.height,
    Math.sqrt(MAX_EXPORT_PIXELS / (scene.width * scene.height)),
  );
  const scale = Math.min(requiredScale, maximumScale);
  return {
    width: Math.max(1, Math.round(scene.width * scale)),
    height: Math.max(1, Math.round(scene.height * scale)),
    scale,
    meetsMinimum: scale + 0.0001 >= requiredScale,
  };
};

export const getSceneConfig = (count, format = "auto", custom = {}) => {
  const original = count <= 1
    ? { width: 900, height: 1100, baseCardWidth: 610 }
    : count === 2
      ? { width: 1200, height: 950, baseCardWidth: 540 }
      : count === 3
        ? { width: 1450, height: 950, baseCardWidth: 470 }
        : { width: 1800, height: 1350, baseCardWidth: 430 };
  if (format === "auto") return original;
  const dimensions = format === "custom" ? custom : FORMATS.find((item) => item.id === format);
  return { ...original, width: dimensions?.width || 1600, height: dimensions?.height || 900 };
};

export const measureImage = (image) => {
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  const full = { x: 0, y: 0, width, height };
  // Cap the scan, but map back to source pixels with a small safety margin.
  const factor = Math.min(1, 1600 / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * factor));
  canvas.height = Math.max(1, Math.round(height * factor));
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) return { width, height, bounds: full };
  try {
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
    let left = canvas.width;
    let top = canvas.height;
    let right = -1;
    let bottom = -1;
    for (let y = 0; y < canvas.height; y += 1) {
      for (let x = 0; x < canvas.width; x += 1) {
        if (data[(y * canvas.width + x) * 4 + 3] <= 4) continue;
        left = Math.min(left, x);
        top = Math.min(top, y);
        right = Math.max(right, x);
        bottom = Math.max(bottom, y);
      }
    }
    if (right < left) return { width, height, bounds: full };
    const x = Math.max(0, Math.floor(left / factor) - 2);
    const y = Math.max(0, Math.floor(top / factor) - 2);
    const endX = Math.min(width, Math.ceil((right + 1) / factor) + 2);
    const endY = Math.min(height, Math.ceil((bottom + 1) / factor) + 2);
    return { width, height, bounds: { x, y, width: endX - x, height: endY - y } };
  } catch {
    // Opaque / unreadable images still get useful dimensions, without cropping.
    return { width, height, bounds: full };
  }
};

export const getImageCrop = (metrics, trim = true) => {
  if (!metrics) return { x: 0, y: 0, width: 1, height: 1.45 };
  return trim ? metrics.bounds : { x: 0, y: 0, width: metrics.width, height: metrics.height };
};

export const getContentArea = (scene, { showTitle = true, showBrand = true, titleInset = scene.height * 0.12 } = {}) => {
  const margin = Math.min(scene.width, scene.height) * 0.025;
  const top = Math.max(margin, showTitle ? titleInset : margin);
  const bottom = Math.max(margin, showBrand ? scene.height * 0.065 : margin);
  return { x: margin, y: top, width: Math.max(1, scene.width - margin * 2), height: Math.max(1, scene.height - top - bottom) };
};

export const rotatedSize = (width, height, rotation) => {
  const radians = rotation * Math.PI / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  return { width: width * cos + height * sin, height: width * sin + height * cos };
};

const getShapes = (assets, images, trim) => {
  const referenceWidth = images[assets[0]?.id]?.width || Object.values(images)[0]?.width || 1;
  return assets.map((asset) => {
    const crop = getImageCrop(images[asset.id], trim);
    return images[asset.id]
      ? { width: crop.width / referenceWidth, height: crop.height / referenceWidth }
      : { width: 1, height: 1.45 };
  });
};

const fitSlots = (slots, assets, scene, images, trim, area, centerAnchor = false) => {
  const shapes = getShapes(assets, images, trim);
  const sizes = slots.map((slot, index) => {
    return rotatedSize(slot.size * shapes[index].width, slot.size * shapes[index].height, slot.rotation);
  });
  const left = Math.min(...slots.map((slot, i) => slot.x - sizes[i].width / 2));
  const right = Math.max(...slots.map((slot, i) => slot.x + sizes[i].width / 2));
  const top = Math.min(...slots.map((slot, i) => slot.y - sizes[i].height / 2));
  const bottom = Math.max(...slots.map((slot, i) => slot.y + sizes[i].height / 2));
  const groupWidth = centerAnchor ? Math.max(Math.abs(left), Math.abs(right)) * 2 : right - left;
  const groupHeight = centerAnchor ? Math.max(Math.abs(top), Math.abs(bottom)) * 2 : bottom - top;
  const unit = Math.min(area.width / groupWidth, area.height / groupHeight);
  return slots.map((slot, index) => ({
    ...assets[index], kind: "card",
    x: area.x + area.width / 2 + (slot.x - (centerAnchor ? 0 : (left + right) / 2)) * unit,
    y: area.y + area.height / 2 + (slot.y - (centerAnchor ? 0 : (top + bottom) / 2)) * unit,
    scale: slot.size * shapes[index].width * unit / scene.baseCardWidth,
    nativeWidthRatio: shapes[index].width,
    rotation: slot.rotation,
    z: 10 + index,
    glowMode: "inherit", glowColor: "#80caff", rarityOverride: "auto",
  }));
};

const packCardRow = (indices, extents, gap, alignment, left = 0, top = 0) => {
  const width = indices.reduce((sum, index) => sum + extents[index].width, 0) + gap * Math.max(0, indices.length - 1);
  const height = Math.max(...indices.map((index) => extents[index].height), 0);
  let cursor = left;
  const positions = indices.map((index) => {
    const size = extents[index];
    const position = {
      index, x: cursor + size.width / 2,
      y: top + (alignment === "bottom" ? height - size.height / 2 : height / 2),
    };
    cursor += size.width + gap;
    return position;
  });
  return { positions, width, height };
};

const balancedRows = (indices, columns) => {
  const rowCount = Math.ceil(indices.length / columns);
  const size = Math.floor(indices.length / rowCount);
  const extra = indices.length % rowCount;
  let start = 0;
  return Array.from({ length: rowCount }, (_, row) => {
    const length = size + (row < extra ? 1 : 0);
    const result = indices.slice(start, start + length);
    start += length;
    return result;
  });
};

const createSpotlightSlots = (shapes, area, gap, tilt, alignment) => {
  const supports = shapes.length - 1;
  const portrait = area.width / area.height < 0.9;
  const mainRotation = -3 * tilt;
  const rotations = shapes.map((_, index) => index === 0 ? mainRotation : (index % 2 === 1 ? 4 : -4) * tilt);
  const extents = shapes.map((shape, index) => rotatedSize(shape.width, shape.height, rotations[index]));
  const maxSupportHeight = Math.max(...extents.slice(1).map((size) => size.height));
  const preferredSpan = supports >= 9 ? 3 : 2;
  const candidates = [];

  for (let columns = 1; columns <= supports; columns += 1) {
    if (portrait) {
      // A featured card above the collection is more natural on a tall canvas.
      const rows = balancedRows(Array.from({ length: supports }, (_, i) => i + 1), columns)
        .map((indices) => packCardRow(indices, extents, gap, alignment));
      const gridWidth = Math.max(...rows.map((row) => row.width));
      const mainSize = Math.max(1.4, Math.min(preferredSpan, gridWidth * 0.65 / extents[0].width));
      const mainWidth = extents[0].width * mainSize;
      const mainHeight = extents[0].height * mainSize;
      const width = Math.max(mainWidth, gridWidth);
      const slots = [{ x: width / 2, y: mainHeight / 2, size: mainSize, rotation: mainRotation }];
      let rowTop = mainHeight + gap;
      rows.forEach((row) => {
        const rowY = rowTop;
        row.positions.forEach((position) => {
          slots[position.index] = { x: (width - row.width) / 2 + position.x, y: rowY + position.y, size: 1, rotation: rotations[position.index] };
        });
        rowTop += row.height + gap;
      });
      candidates.push({ slots, width, height: rowTop - gap, span: mainHeight / maxSupportHeight, orphans: rows.filter((row) => row.positions.length === 1).length });
      continue;
    }

    for (let span = 2; span <= preferredSpan; span += 1) {
      // Start with a few rows beside the hero. Once those rows are full, the
      // remaining cards can use the entire width underneath both sections.
      const rightCount = Math.min(supports, span * columns);
      const rightIndices = Array.from({ length: rightCount }, (_, i) => i + 1);
      const rightRows = balancedRows(rightIndices, columns).map((indices) => packCardRow(indices, extents, gap, alignment));
      let nextIndex = rightCount + 1;
      const rightWidth = Math.max(...rightRows.map((row) => row.width));
      const rightHeight = rightRows.reduce((sum, row) => sum + row.height, 0) + gap * (rightRows.length - 1);
      const mainHeight = Math.max(maxSupportHeight * 1.4, rightHeight);
      const mainSize = mainHeight / extents[0].height;
      const mainWidth = extents[0].width * mainSize;
      const width = mainWidth + gap + rightWidth;
      const slots = [{ x: mainWidth / 2, y: mainHeight / 2, size: mainSize, rotation: mainRotation }];
      let rowTop = (mainHeight - rightHeight) / 2;
      rightRows.forEach((row) => {
        const rowY = rowTop;
        row.positions.forEach((position) => {
          slots[position.index] = { x: mainWidth + gap + position.x, y: rowY + position.y, size: 1, rotation: rotations[position.index] };
        });
        rowTop += row.height + gap;
      });

      rowTop = mainHeight + gap;
      let orphans = rightRows.filter((row) => row.positions.length === 1).length;
      while (nextIndex < shapes.length) {
        const indices = [];
        let rowWidth = 0;
        while (nextIndex < shapes.length) {
          const nextWidth = rowWidth + (indices.length ? gap : 0) + extents[nextIndex].width;
          if (indices.length && nextWidth > width + 0.0001) break;
          indices.push(nextIndex);
          rowWidth = nextWidth;
          nextIndex += 1;
        }
        const row = packCardRow(indices, extents, gap, alignment, (width - rowWidth) / 2, rowTop);
        if (indices.length === 1) orphans += 1;
        row.positions.forEach((position) => {
          slots[position.index] = { x: position.x, y: position.y, size: 1, rotation: rotations[position.index] };
        });
        rowTop += row.height + gap;
      }
      candidates.push({ slots, width, height: Math.max(mainHeight, rowTop - gap), span: rightRows.length, orphans });
    }
  }

  let best = candidates[0];
  let bestScore = -Infinity;
  candidates.forEach((candidate) => {
    const unit = Math.min(area.width / candidate.width, area.height / candidate.height);
    const fill = candidate.width * candidate.height * unit * unit / (area.width * area.height);
    // Keep companions readable while giving the featured card enough weight
    // to span several rows. Penalize isolated trailing cards strongly.
    const prominence = Math.min(1, candidate.span / preferredSpan);
    const score = unit * (0.8 + fill * 0.2) * (0.6 + prominence * 0.4) / (1 + candidate.orphans * 0.6);
    if (score > bestScore) { bestScore = score; best = candidate; }
  });
  return best.slots;
};

const createOrbitSlots = (shapes, area, gap, tilt, alignment) => {
  const supports = shapes.length - 1;
  const rotations = shapes.map((_, index) => index === 0 ? 0 : (index % 2 ? -7 : 7) * tilt);
  const extents = shapes.map((shape, index) => rotatedSize(shape.width, shape.height, rotations[index]));
  const cellWidth = Math.max(...extents.slice(1).map((size) => size.width));
  const cellHeight = Math.max(...extents.slice(1).map((size) => size.height));
  let best;
  let bestScore = -Infinity;

  // A central pocket surrounded by balanced rows creates an orbit without
  // forcing the entire collection into two increasingly tall columns.
  for (let rows = 3; rows <= Math.floor(supports / 2); rows += 1) {
    for (let span = 1; span <= Math.min(3, rows - 2); span += 1) {
      if ((rows - span) % 2 !== 0) continue;
      const outerRows = (rows - span) / 2;
      const mainHeight = Math.max(cellHeight * 1.5, span * cellHeight + (span - 1) * gap);
      const mainSize = mainHeight / extents[0].height;
      const mainWidth = extents[0].width * mainSize;
      for (let sideColumns = 1; sideColumns <= Math.ceil(supports / rows); sideColumns += 1) {
        const sideWidth = sideColumns * cellWidth + (sideColumns - 1) * gap;
        const width = mainWidth + 2 * (gap + sideWidth);
        const outerColumns = Math.max(2, Math.floor((width + gap) / (cellWidth + gap)));
        const capacities = Array.from({ length: rows }, (_, row) => row < outerRows || row >= outerRows + span ? outerColumns : sideColumns * 2);
        if (capacities.reduce((sum, capacity) => sum + capacity, 0) < supports) continue;
        const height = mainHeight + 2 * outerRows * (cellHeight + gap);
        const unit = Math.min(area.width / width, area.height / height);
        const fill = width * height * unit * unit / (area.width * area.height);
        const score = unit * (0.8 + fill * 0.2) * (span === 1 ? 0.94 : 1);
        if (score <= bestScore) continue;

        const counts = capacities.map(() => 2);
        for (let remaining = supports - rows * 2; remaining > 0; remaining -= 1) {
          let selected = -1;
          counts.forEach((count, row) => {
            if (count >= capacities[row]) return;
            if (selected < 0 || count / capacities[row] < counts[selected] / capacities[selected]) selected = row;
          });
          counts[selected] += 1;
        }
        const slots = [{ x: 0, y: 0, size: mainSize, rotation: 0 }];
        let nextIndex = 1;
        counts.forEach((count, row) => {
          const indices = Array.from({ length: count }, (_, i) => nextIndex + i);
          nextIndex += count;
          const above = row < outerRows;
          const below = row >= outerRows + span;
          if (above || below) {
            const distance = above ? outerRows - row : row - outerRows - span + 1;
            const y = (above ? -1 : 1) * (mainHeight / 2 + gap + cellHeight / 2 + (distance - 1) * (cellHeight + gap));
            const packed = packCardRow(indices, extents, gap, alignment);
            packed.positions.forEach((position) => {
              slots[position.index] = { x: position.x - packed.width / 2, y: y + position.y - packed.height / 2, size: 1, rotation: rotations[position.index] };
            });
          } else {
            const centralRow = row - outerRows;
            const leftCount = Math.floor(count / 2) + (count % 2 && centralRow % 2 === 0 ? 1 : 0);
            const y = span === 1 ? 0 : -mainHeight / 2 + cellHeight / 2 + centralRow * (mainHeight - cellHeight) / (span - 1);
            [indices.slice(0, leftCount), indices.slice(leftCount)].forEach((group, side) => {
              const packed = packCardRow(group, extents, gap, alignment);
              const x = side === 0 ? -mainWidth / 2 - gap - packed.width : mainWidth / 2 + gap;
              packed.positions.forEach((position) => {
                slots[position.index] = { x: x + position.x, y: y + position.y - packed.height / 2, size: 1, rotation: rotations[position.index] };
              });
            });
          }
        });
        best = slots;
        bestScore = score;
      }
    }
  }
  return best;
};

export const createCardLayout = (assets, scene, options = {}) => {
  const { preset = "classic", spacing = 1, tilt = 1, images = {}, trim = true, alignment = "bottom" } = options;
  const count = assets.length;
  if (!count) return [];
  // The raised right-hand card can use the space beside the title. Reserving
  // a full-width header here unnecessarily shrinks this diagonal composition.
  const classicPair = preset === "classic" && count === 2 && scene.width / scene.height > 1.1;
  const area = getContentArea(scene, classicPair ? { ...options, showTitle: false } : options);
  const shapes = getShapes(assets, images, trim);
  const maxWidth = Math.max(...shapes.map((shape) => shape.width));
  const maxHeight = Math.max(...shapes.map((shape) => shape.height));
  // Negative spacing is a fraction of the smallest card's visible size,
  // giving the overlap range real reach while keeping every row step positive.
  const companionSize = preset === "spotlight" && count > 1 && count <= 3
    ? count === 2 ? 0.85 : 0.68
    : preset === "orbit" && count > 1 && count <= 7 ? 0.78 : 1;
  const overlapSize = Math.min(...shapes.map((shape) => Math.min(shape.width, shape.height))) * companionSize;
  const gap = spacing < 0 ? spacing * overlapSize : 0.10 * spacing;
  let slots;

  // Preserve the much-loved two / three-card composition at its original ratio.
  if (preset === "classic" && count <= 3 && scene.width / scene.height > 1.1) {
    const angles = assets.map((_, index) => count === 2 ? (index === 0 ? -6 : 6) * tilt : (index - (count - 1) / 2) * 8 * tilt);
    const heights = shapes.map((shape, index) => rotatedSize(shape.width, shape.height, angles[index]).height);
    slots = assets.map((_, index) => ({
      x: (index - (count - 1) / 2) * (maxWidth + gap),
      y: (count === 2 ? (index === 0 ? 0.14 : -0.14) : 0) + (alignment === "bottom" ? (Math.max(...heights) - heights[index]) / 2 : 0),
      rotation: angles[index],
      size: 1,
    }));
  } else if (preset === "orbit" && count > 7) {
    slots = createOrbitSlots(shapes, area, gap, tilt, alignment);
  } else if (preset === "orbit" && count > 1) {
    const portrait = area.width / area.height < 0.9;
    const small = 0.78;
    slots = [{ x: 0, y: 0, size: 1.12, rotation: 0 }];
    const mainExtent = rotatedSize(shapes[0].width * 1.12, shapes[0].height * 1.12, 0);
    for (let i = 1; i < count; i += 1) {
      const sideSign = i % 2 === 1 ? -1 : 1;
      const row = Math.floor((i - 1) / 2);
      slots.push({ x: 0, y: 0, size: small, rotation: sideSign * (8 + row * 2) * tilt });
    }
    const extents = slots.map((slot, i) => rotatedSize(shapes[i].width * slot.size, shapes[i].height * slot.size, slot.rotation));
    const groups = portrait
      ? count <= 3 ? [[1], count === 3 ? [2] : []] : [[1, 2], Array.from({ length: count - 3 }, (_, i) => i + 3)]
      : [assets.map((_, i) => i).filter((i) => i > 0 && i % 2 === 1), assets.map((_, i) => i).filter((i) => i > 0 && i % 2 === 0)];
    groups.forEach((group, sideIndex) => {
      if (!group.length) return;
      const sign = sideIndex === 0 ? -1 : 1;
      const total = group.reduce((sum, index) => sum + (portrait ? extents[index].width : extents[index].height), 0) + gap * (group.length - 1);
      let cursor = -total / 2;
      const rowHeight = Math.max(...group.map((index) => extents[index].height));
      group.forEach((index) => {
        const extent = extents[index];
        if (portrait) {
          slots[index].x = cursor + extent.width / 2;
          slots[index].y = sign * (mainExtent.height / 2 + gap + rowHeight / 2);
          cursor += extent.width + gap;
        } else {
          slots[index].x = sign * (mainExtent.width / 2 + gap + extent.width / 2);
          slots[index].y = cursor + extent.height / 2 + (group.length === 1 && count === 3 ? sign * maxHeight * 0.12 : 0);
          cursor += extent.height + gap;
        }
      });
    });
  } else if (preset === "spotlight" && count > 3) {
    slots = createSpotlightSlots(shapes, area, gap, tilt, alignment);
  } else if (preset === "spotlight" && count > 1) {
    const supports = count - 1;
    const portrait = area.width / area.height < 0.9;
    const columns = portrait ? Math.min(3, supports) : Math.ceil(Math.sqrt(supports));
    const rows = Math.ceil(supports / columns);
    const small = supports === 1 ? 0.85 : 0.68;
    const supportExtent = rotatedSize(maxWidth * small, maxHeight * small, 4 * tilt);
    const mainExtent = rotatedSize(maxWidth * 1.3, maxHeight * 1.3, 3 * tilt);
    const supportWidth = columns * supportExtent.width + (columns - 1) * gap;
    const supportHeight = rows * supportExtent.height + (rows - 1) * gap;
    slots = [{ x: 0, y: 0, size: 1.3, rotation: -3 * tilt }];
    for (let i = 0; i < supports; i += 1) {
      const row = Math.floor(i / columns);
      const rowCount = Math.min(columns, supports - row * columns);
      const x = (i % columns - (rowCount - 1) / 2) * (supportExtent.width + gap);
      const actualExtent = rotatedSize(shapes[i + 1].width * small, shapes[i + 1].height * small, 4 * tilt);
      const y = (row - (rows - 1) / 2) * (supportExtent.height + gap) + (alignment === "bottom" ? (supportExtent.height - actualExtent.height) / 2 : 0);
      slots.push({
        x: portrait ? x : mainExtent.width / 2 + gap + supportWidth / 2 + x,
        y: portrait ? mainExtent.height / 2 + gap + supportHeight / 2 + y : y,
        size: small,
        rotation: (i % 2 === 0 ? 4 : -4) * tilt,
      });
    }
  } else {
    // Try every column count. Choose the arrangement with the largest visible
    // cards after fitting, including rotation and differently-shaped assets.
    let bestScore = -Infinity;
    for (let columns = 1; columns <= count; columns += 1) {
      const rows = balancedRows(assets.map((_, i) => i), columns);
      const rotations = [];
      rows.forEach((indices) => {
        indices.forEach((i, column) => {
          const t = indices.length > 1 ? column / (indices.length - 1) * 2 - 1 : 0;
          rotations[i] = count === 1 || preset === "gallery" ? 0 : preset === "fan" ? t * 12 * tilt : (i % 2 === 0 ? -5 : 5) * tilt;
        });
      });
      const extents = rotations.map((angle, i) => rotatedSize(shapes[i].width, shapes[i].height, angle));
      const candidate = [];
      let rowTop = 0;
      rows.forEach((indices) => {
        const rowCount = indices.length;
        const rowExtents = indices.map((i) => extents[i]);
        const rowWidth = rowExtents.reduce((sum, size) => sum + size.width, 0) + gap * (rowCount - 1);
        const rowHeight = Math.max(...rowExtents.map((size) => size.height));
        const rowSlots = rowExtents.map((size, column) => {
          const i = indices[column];
          const t = rowCount > 1 ? column / (rowCount - 1) * 2 - 1 : 0;
          const offset = preset === "fan" ? t * t * 0.17 * tilt : preset === "cascade" ? t * 0.25 * tilt : preset === "gallery" ? 0 : (i % 2 ? 0.06 : -0.06) * tilt;
          return { y: (alignment === "bottom" ? (rowHeight - size.height) / 2 : 0) + offset, size: 1, rotation: rotations[i] };
        });
        const top = Math.min(...rowSlots.map((slot, i) => slot.y - rowExtents[i].height / 2));
        const bottom = Math.max(...rowSlots.map((slot, i) => slot.y + rowExtents[i].height / 2));
        let cursor = -rowWidth / 2;
        const rowY = rowTop;
        rowSlots.forEach((slot, column) => {
          const width = rowExtents[column].width;
          candidate.push({ ...slot, x: cursor + width / 2, y: rowY + slot.y - top });
          cursor += width + gap;
        });
        rowTop += bottom - top + gap;
      });
      const result = fitSlots(candidate, assets, scene, images, trim, area);
      const orphans = rows.filter((row) => row.length === 1).length;
      const score = result[0].scale / (1 + (count > 3 ? orphans * 0.6 : 0));
      if (score > bestScore) { bestScore = score; slots = candidate; }
    }
  }

  const result = fitSlots(slots, assets, scene, images, trim, area, preset === "orbit");
  // Single cards need breathing room instead of filling the whole safe area.
  if (count === 1) result[0].scale *= 0.9;
  return result;
};

export const getCardClusterBounds = (elements, scene, images, trim) => {
  const cards = elements.filter((element) => element.kind === "card");
  if (!cards.length) return null;
  const boxes = cards.map((element) => {
    const crop = getImageCrop(images[element.id], trim);
    const width = scene.baseCardWidth * element.scale;
    const size = rotatedSize(width, width * crop.height / crop.width, element.rotation);
    return { left: element.x - size.width / 2, right: element.x + size.width / 2, top: element.y - size.height / 2, bottom: element.y + size.height / 2 };
  });
  const left = Math.min(...boxes.map((box) => box.left));
  const right = Math.max(...boxes.map((box) => box.right));
  const top = Math.min(...boxes.map((box) => box.top));
  const bottom = Math.max(...boxes.map((box) => box.bottom));
  return { left, right, top, bottom, width: right - left, height: bottom - top, x: (left + right) / 2, y: (top + bottom) / 2 };
};

// Scale the composition as one object. Fitting it as a group preserves relative
// sizes and spacing instead of shrinking / clamping each card independently.
export const scaleCardCluster = (elements, requestedFactor, scene, images, trim, keepInside = true) => {
  const bounds = getCardClusterBounds(elements, scene, images, trim);
  if (!bounds) return { elements, factor: 1 };
  const margin = Math.min(scene.width, scene.height) * 0.015;
  const factor = keepInside
    ? Math.min(requestedFactor, (scene.width - margin * 2) / bounds.width, (scene.height - margin * 2) / bounds.height)
    : requestedFactor;
  const halfWidth = bounds.width * factor / 2;
  const halfHeight = bounds.height * factor / 2;
  const x = keepInside ? clamp(bounds.x, margin + halfWidth, scene.width - margin - halfWidth) : bounds.x;
  const y = keepInside ? clamp(bounds.y, margin + halfHeight, scene.height - margin - halfHeight) : bounds.y;
  return {
    factor,
    elements: elements.map((element) => element.kind === "card" ? {
      ...element,
      x: x + (element.x - bounds.x) * factor,
      y: y + (element.y - bounds.y) * factor,
      scale: element.scale * factor,
    } : element),
  };
};

export const constrainCard = (element, scene, images, trim) => {
  const crop = getImageCrop(images[element.id], trim);
  const width = scene.baseCardWidth * element.scale;
  const size = rotatedSize(width, width * crop.height / crop.width, element.rotation);
  const fit = Math.min(1, scene.width / size.width, scene.height / size.height);
  const halfWidth = size.width * fit / 2;
  const halfHeight = size.height * fit / 2;
  return {
    ...element, scale: element.scale * fit,
    x: clamp(element.x, halfWidth, scene.width - halfWidth),
    y: clamp(element.y, halfHeight, scene.height - halfHeight),
  };
};

export const resolveGlow = (element, settings) => {
  const mode = element.glowMode === "inherit" ? settings.mode : element.glowMode;
  const rarity = RARITIES.find((item) => item.id === (element.rarityOverride === "auto" ? element.rarity : element.rarityOverride));
  return {
    mode, color: mode === "custom" ? (element.glowMode === "custom" ? element.glowColor : settings.color) : rarity?.color || "#edf4ff",
    strength: settings.strength, radius: settings.radius,
  };
};

const rgba = (hex, alpha) => {
  const value = parseInt(hex.replace("#", ""), 16);
  return `rgba(${(value >> 16) & 255},${(value >> 8) & 255},${value & 255},${alpha})`;
};

export const getCardFilter = (glow, pixelRatio = 1) => {
  const shadow = `drop-shadow(0 ${18 * pixelRatio}px ${16 * pixelRatio}px rgba(0,0,0,0.52))`;
  if (glow.mode === "off" || glow.strength <= 0) return shadow;
  return `drop-shadow(0 0 ${glow.radius * 0.35 * pixelRatio}px ${rgba(glow.color, glow.strength * 0.8)}) drop-shadow(0 0 ${glow.radius * pixelRatio}px ${rgba(glow.color, glow.strength * 0.65)}) ${shadow}`;
};
